import { useEffect, useState } from 'react';
import { getOrderAsEmployee } from '../../services/orders';
import { formatDate, formatDateTime, formatCurrency } from '../../utils/formatters';
import { FULFILLMENT_LABELS } from '../../utils/orderConstants';
import './PrintPacklist.css';

const resolveUnitPrice = (item) =>
  parseFloat(
    item.unit_price_dollars ?? item.unit_price ??
    item.product_variant?.price_dollars ?? item.variant?.price_dollars ??
    item.product_variant?.price ?? item.variant?.price ?? 0
  );

/**
 * Standalone print-packlist page.
 * Opened in a new tab by the employee OrderDetail page.
 *
 * URL params:
 *   id — order ID (integer)
 */
const PrintPacklist = () => {
  const params  = new URLSearchParams(window.location.search);
  const orderId = parseInt(params.get('id'), 10);

  const [order, setOrder]       = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!orderId) return;
    getOrderAsEmployee(orderId)
      .then(setOrder)
      .catch(() => setLoadError('Could not load order. Make sure you are logged in.'));
  }, [orderId]);

  // Auto-open print dialog once the order is ready
  useEffect(() => {
    if (!order) return;
    const timer = setTimeout(() => window.print(), 800);
    return () => clearTimeout(timer);
  }, [order]);

  if (!orderId) {
    return (
      <div className="print-packlist-page print-packlist--error">
        <p>No order ID provided.</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="print-packlist-page print-packlist--error">
        <p>{loadError}</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="print-packlist-page print-packlist--loading">
        <p>Loading order…</p>
      </div>
    );
  }

  const {
    order_number,
    fulfillment_type,
    delivery_date,
    delivery_address_display,
    delivery_address,
    special_instructions,
    items,
    created_at,
    company_detail,
    session_detail,
  } = order;

  const isPickup = fulfillment_type === 'pickup';

  const addressDisplay =
    delivery_address_display ||
    (delivery_address && typeof delivery_address === 'object' ? delivery_address : null);

  const orderTotal = (items || []).reduce((sum, item) => {
    const unitPrice = resolveUnitPrice(item);
    const qty = parseInt(item.quantity, 10) || 0;
    return sum + parseFloat(item.subtotal_dollars || item.subtotal || unitPrice * qty);
  }, 0);

  return (
    <div className="print-packlist-page">

      {/* Screen-only toolbar */}
      <div className="print-packlist__toolbar no-print">
        <span className="print-packlist__toolbar-label">Packlist — {order_number}</span>
        <button
          type="button"
          className="print-packlist__print-btn"
          onClick={() => window.print()}
        >
          🖨 Print
        </button>
      </div>

      {/* ── Printable document ── */}
      <div className="print-packlist">

        {/* Header */}
        <div className="print-packlist__header">
          <div className="print-packlist__brand">
            <p className="print-packlist__brand-name">Deep Roots Milling</p>
            <p className="print-packlist__doc-type">Pack List</p>
          </div>
          <div className="print-packlist__order-meta">
            <p className="print-packlist__order-number">{order_number}</p>
            <p className="print-packlist__order-date">Placed {formatDateTime(created_at)}</p>
          </div>
        </div>

        <hr className="print-packlist__rule" />

        {/* Order info */}
        <dl className="print-packlist__info">
          {company_detail && (
            <div className="print-packlist__info-row">
              <dt>Customer</dt>
              <dd>{company_detail.company_name}</dd>
            </div>
          )}
          {fulfillment_type && (
            <div className="print-packlist__info-row">
              <dt>Fulfillment</dt>
              <dd>{FULFILLMENT_LABELS[fulfillment_type] || fulfillment_type}</dd>
            </div>
          )}
          {session_detail && (
            <div className="print-packlist__info-row">
              <dt>Mill Session</dt>
              <dd>{session_detail.label}</dd>
            </div>
          )}
          {delivery_date && (
            <div className="print-packlist__info-row">
              <dt>{isPickup ? 'Pickup Date' : 'Delivery Date'}</dt>
              <dd>{formatDate(delivery_date)}</dd>
            </div>
          )}
          {addressDisplay && (
            <div className="print-packlist__info-row">
              <dt>Delivery Address</dt>
              <dd>
                {addressDisplay.street_address}
                {addressDisplay.street_address_2 && <>, {addressDisplay.street_address_2}</>}
                {', '}
                {addressDisplay.city}, {addressDisplay.state} {addressDisplay.zip_code}
                {addressDisplay.notes && (
                  <div className="print-packlist__address-notes">{addressDisplay.notes}</div>
                )}
              </dd>
            </div>
          )}
        </dl>

        <hr className="print-packlist__rule" />

        {/* Line items */}
        <table className="print-packlist__table">
          <thead>
            <tr>
              <th className="print-packlist__th print-packlist__th--product">Product</th>
              <th className="print-packlist__th print-packlist__th--variant">Variant</th>
              <th className="print-packlist__th print-packlist__th--qty">Qty</th>
              <th className="print-packlist__th print-packlist__th--price">Unit Price</th>
              <th className="print-packlist__th print-packlist__th--subtotal">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((item) => {
              const productName = item.product_variant?.product_name || item.product?.name || '—';
              const variantName = item.product_variant?.variant_name || item.variant?.variant_name || '';
              const unitPrice = resolveUnitPrice(item);
              const qty = parseInt(item.quantity, 10) || 0;
              const subtotal = parseFloat(item.subtotal_dollars || item.subtotal || unitPrice * qty);

              return (
                <tr key={item.id} className="print-packlist__row">
                  <td className="print-packlist__td">{productName}</td>
                  <td className="print-packlist__td print-packlist__td--muted">{variantName || '—'}</td>
                  <td className="print-packlist__td print-packlist__td--center">{qty}</td>
                  <td className="print-packlist__td print-packlist__td--right">{formatCurrency(unitPrice)}</td>
                  <td className="print-packlist__td print-packlist__td--right">{formatCurrency(subtotal)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="print-packlist__total-row">
              <td colSpan={4} className="print-packlist__total-label">Order Total</td>
              <td className="print-packlist__total-amount">{formatCurrency(orderTotal)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Special instructions */}
        {special_instructions && (
          <>
            <hr className="print-packlist__rule" />
            <div className="print-packlist__instructions">
              <p className="print-packlist__instructions-label">Special Instructions</p>
              <p className="print-packlist__instructions-text">{special_instructions}</p>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="print-packlist__footer">
          <p>Printed {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

      </div>
    </div>
  );
};

export default PrintPacklist;
